/**
 * Password Validator (conf and functions).
 */
export class PasswordValidator {
  /**
   * Password validator: Checks that the password is not empty.
   *
   * @param   {string}  password - The password value.
   * @returns {boolean} Wether the filled validation is ok or not.
   */
  static isFilled(password) {
    return password.length > 0;
  }

  /**
   * Password min-length validator (excludes all accented and forbidden chars from this count).
   *
   * @param   {string}  password - The password value.
   * @returns {boolean} Wether the min-length validation is ok or not.
   */
  static isMinLengthValid(password) {
    const result = password.match(/[\x21-\x7E]/g);

    if (!result) {
      return false;
    }

    return result.length >= 8;
  }

  /**
   * Password upper-case validator.
   *
   * @param   {string}  password - The password value.
   * @returns {boolean} Wether the upper-case validation is ok or not.
   */
  static isUpperCaseValid(password) {
    const result = password.match(/[A-Z]/g);

    if (!result) {
      return false;
    }

    return result.length >= 1;
  }

  /**
   * Password lower-case validator.
   *
   * @param   {string}  password - The password value.
   * @returns {boolean} Wether the lower-case validation is ok or not.
   */
  static isLowerCaseValid(password) {
    const result = password.match(/[a-z]/g);

    if (!result) {
      return false;
    }

    return result.length >= 1;
  }

  /**
   * Gives the position of the first space (-1 if not found).
   *
   * @param   {string}  password - The password value.
   * @returns {int | boolean} The position of the first space.
   */
  static getFirstSpacePosition(password) {
    const position = password.search(/\s/g);

    if (position === -1) {
      return true;
    }

    return position;
  }

  /**
   * Gives the position of the first accented char (-1 if not found).
   *
   * @param   {string}  password - The password value.
   * @returns {int | boolean} The position of the first accented char.
   */
  static getFirstAccentedCharPosition(password) {
    const position = password.search(/[\u00C0-\u107E]/g);

    if (position === -1) {
      return true;
    }

    return position;
  }

  /**
   * Gives the position of the first special char (-1 if not found).
   *
   * @param   {string}  password - The password value.
   * @returns {int | boolean} The position of the first special char.
   */
  static getFirstSpecialCharPosition(password) {
    const position = password.search(/[\u0000-\u001F]|[\u007F-\u00BF]|[\u017F-\uFFFF]/g);

    if (position === -1) {
      return true;
    }

    return position;
  }

  /**
   * Gives the position of the first special char (-1 if not found).
   *
   * @returns {int} The position of the first special char.
   */
  static getConf() {
    return {
      accentedChars: (password) => PasswordValidator.getFirstAccentedCharPosition(password),
      digits: 1,
      filled: (password) => PasswordValidator.isFilled(password),
      lowercase: (password) => PasswordValidator.isLowerCaseValid(password),
      max: 36,
      min: (password) => PasswordValidator.isMinLengthValid(password),
      uppercase: (password) => PasswordValidator.isUpperCaseValid(password),
      space: (password) => PasswordValidator.getFirstSpacePosition(password),
      specialChars: (password) => PasswordValidator.getFirstSpecialCharPosition(password),
    };
  }
}
